import React, { ChangeEvent, KeyboardEvent, MouseEvent, useState, useEffect, useRef } from 'react';
import ResultsSlider from '@components/acca/results/common/resultsSlider';
import Icon from '@components/common/assets';
import Text from '@components/common/text';
import { useToast } from '@components/common/toast';
import Modal from '@components/common/modal';
import AccaToast from '@components/acca/results/resultList/AccaToast';
import EmptyResult from '@components/acca/results/resultList/emptyResult';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useAccaTestResultStore } from '@stores/AccaTestResultStore';
import { useDeleteApplyHistory, useUpdateApplyName } from '@repository/acc/useAcca';
import { AccResultRecordVO } from '@domain/vo/acca/v1/test/result/AccResultVO';
import { accAnalysisStatusMap } from '@domain/constant/match/profile/acc';
import { AnalysisType, analysisTypeStringMap } from '@domain/constant/analysisType';
import Colors from '@domain/constant/colors';
import { FontStyle } from '@domain/constant/fontStyle';
import DateFormat from '@utils/date/format';
import useModals from '@utils/hooks/useModals';
import useClickAway from '@utils/hooks/useClickOutside';
import classnames from 'classnames/bind';
import styles from './resultList.module.scss';
const cx = classnames.bind(styles);
interface ResultItemProps {
  analysisType: AnalysisType;
  resultData: AccResultRecordVO;
  dateTime: string;
  onClick: () => void;
  isOn: boolean;
}

interface DeleteModalProps {
  visible: boolean;
  onDelete: () => void;
  onClose: () => void;
}
const DeleteModal = ({ visible, onDelete, onClose }: DeleteModalProps) => {
  return (
    <Modal visible={visible}>
      <div className={cx('modalArea')}>
        <Text fontStyle={FontStyle.H4_B} color={Colors.C_JOBDA_BLACK}>
          역검 결과표를 삭제하시겠어요?
        </Text>
        <Text className={cx('desc')} fontStyle={FontStyle.B2_M} color={Colors.C_COOL_GRAY_70}>
          삭제한 결과표는 복구할 수 없습니다.
        </Text>
        <div className={cx('btnArea')}>
          <button className={cx('btn', 'close')} onClick={onClose}>
            취소
          </button>
          <button className={cx('btn', 'delete')} onClick={onDelete}>
            삭제
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ResultItem = ({ analysisType, resultData, dateTime, onClick, isOn }: ResultItemProps) => {
  const Toast = useToast();
  const { openModal, isVisible, closeModal } = useModals(['MENU', 'DELETE']);
  const fetchAccResults = useAccaTestResultStore((state) => state.fetchAccResults);
  const { mutateAsync: updateAccName } = useUpdateApplyName();
  const { mutateAsync: deleteApplyRecent } = useDeleteApplyHistory();
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [errorImg, setErrorImg] = useState(false);
  const [saveLatestTitle, setSaveLatestTitle] = useState(resultData.name ?? '역검 결과표');
  const inputRef = useRef<HTMLInputElement>(null);

  const type = accAnalysisStatusMap[analysisType];
  const badge = analysisTypeStringMap[analysisType];
  const viewDateTime = DateFormat.format(new Date(dateTime), 'yyyy.MM.dd HH:mm');

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const clickMenuBtn = (e: MouseEvent) => {
    e.stopPropagation();
    if (isVisible('MENU')) {
      closeModal();
    } else {
      openModal('MENU');
    }
  };

  useClickAway(() => {
    if (isVisible('MENU')) closeModal();
  }, inputRef);

  const handleEditClick = () => {
    setIsEditing(true);
    setNewTitle('');
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length <= 10) {
      setNewTitle(event.target.value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSaveClick();
    }
  };

  const handleBlur = () => {
    if (isEditing) {
      handleSaveClick();
    }
  };

  const handleSaveClick = async () => {
    setIsEditing(false);
    if (!newTitle || newTitle === saveLatestTitle) return;

    await updateAccName(
      { sn: resultData.sn, name: newTitle },
      {
        onSuccess: () => {
          setSaveLatestTitle(newTitle);
          fetchAccResults();
        },
        onError: (error: any) => Toast({ type: 'error', iconType: 'info', content: error.response.data.message }),
      },
    );
  };

  const onClickRemoveButton = async () => {
    await deleteApplyRecent([resultData.sn], {
      onSuccess: () => {
        Toast({ type: 'success', iconType: 'info', content: '역검 결과표가 삭제되었습니다.' });
        fetchAccResults();
      },
      onError: (error: any) => Toast({ type: 'error', iconType: 'info', content: error.response.data.message }),
    });
  };

  return (
    <>
      <a className={cx('btnResult', { on: isOn })} role="button" onClick={onClick}>
        {/* 결과 버튼 */}
        <div className={cx('resultArea')}>
          {/* 썸네일 */}
          <div className={cx('imgArea')}>
            {resultData.thumbnailUrl && !errorImg ? (
              <img
                src={resultData.thumbnailUrl}
                onError={() => setErrorImg(true)}
                alt="역검 결과표"
                width={326}
                height={186}
              />
            ) : (
              <div className={cx('noThumbnail')} />
            )}
          </div>
          {/* 역검 결과표 설명 */}
          <div className={cx('infoArea')}>
            <span className={cx('badge', type)}>{badge}</span>
            {isEditing ? (
              <div className={cx('editTitleArea')}>
                <input
                  ref={inputRef}
                  type="text"
                  className={cx('editTitle')}
                  onChange={handleInputChange}
                  value={newTitle}
                  defaultValue={saveLatestTitle}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  placeholder={saveLatestTitle}
                />
                <button className={cx('btnSave', { active: newTitle.length > 0 })} onClick={handleSaveClick}>
                  저장
                </button>
                {/* 글자수 */}
                <Text
                  className={cx('lengthTitle', { active: newTitle.length > 0 })}
                  fontStyle={FontStyle.B4P_M}
                  color={Colors.C_COOL_GRAY_60}
                >
                  {`${newTitle.length} / 10`}
                </Text>
              </div>
            ) : (
              <h1 className={cx('title')}>{newTitle.length ? newTitle : saveLatestTitle}</h1>
            )}
            {dateTime && <span className={cx('dateTime')}>{viewDateTime}</span>}
          </div>
        </div>

        {/* 삭제, 이름 변경 */}
        <div className={cx('optionArea')}>
          <a className={cx('btnOption')} role="button" onClick={clickMenuBtn}>
            <Icon
              name="moreHorizonLight"
              width={32}
              height={32}
              fill={isVisible('MENU') ? Colors.C_JOBDA_BLACK : Colors.C_COOL_GRAY_60}
            />
          </a>
          {isVisible('MENU') && (
            <div className={cx('menuArea')}>
              <a className={cx('btnMenu')} role="button" onClick={handleEditClick}>
                이름 수정
              </a>
              <a className={cx('btnMenu')} role="button" onClick={() => openModal('DELETE')}>
                삭제
              </a>
            </div>
          )}
        </div>
      </a>
      <DeleteModal visible={isVisible('DELETE')} onClose={closeModal} onDelete={onClickRemoveButton} />
    </>
  );
};

const ResultList = () => {
  const accResults = useAccaTestResultStore((state) => state.accResults);
  const accResult = useAccaTestResultStore((state) => state.accResult);
  const setAccResultBySn = useAccaTestResultStore((state) => state.setAccResultBySn);
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  const resultItemEls = accResults?.resultRecords?.map((resultRecord) => {
    const { analysisType, applicationDate, sn } = resultRecord;

    const handleClickResultItem = () => {
      setAccResultBySn(sn);
      if (analysisType !== 'COMPLETE') return AccaToast({ type: analysisType });
    };

    const isOn = sn === accResult?.sn;

    return (
      <ResultItem
        key={sn}
        resultData={resultRecord}
        analysisType={analysisType}
        dateTime={applicationDate}
        onClick={handleClickResultItem}
        isOn={isOn}
      />
    );
  });

  if (!accResults?.resultRecords?.length || !isLogin) {
    return <EmptyResult />;
  }

  return (
    <>
      <div className={cx('sliderArea')}>
        <ResultsSlider resultItemEls={resultItemEls} />
      </div>
    </>
  );
};

export default ResultList;
